import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useProjectNodesApi } from 'api/project-nodes/project-nodes-api';
import type { DataNodeDetail } from 'api/project-nodes/types';
import { resolve } from 'api/resolve';
import { assertDefined } from 'utils/values';

import { buildCandidateRelations, buildGraphNodeView } from '../projectGraphView';
import type { GraphNodeWithRelationsView } from '../types';
import type { TemplateMaps } from '../useTemplateMaps';

export const PROJECT_GRAPH_NODE_DETAIL_QUERY_KEY = 'projectGraphNodeDetail';

/**
 * The identifying prefix of every cached copy of one data node's detail, regardless of which proposal
 * or template-node variant it was fetched under (those trail the key — see the full `queryKey` below).
 * Callers that need to invalidate a node wherever it is cached (e.g. a SignalR `DataNodeUpdated`)
 * should match on this prefix rather than duplicating the literal key.
 */
export function getGraphRootNodeQueryKeyPrefix(projectId: string | undefined, nodeId: string | undefined) {
  return [PROJECT_GRAPH_NODE_DETAIL_QUERY_KEY, projectId, nodeId] as const;
}

/**
 * The graph root, resolved for display: the node itself + the relations it can expand to. Declarative
 * `select` reshapes the raw `DataNodeDetail` into the view-model; gated on the template maps so the
 * blueprint is available to resolve icons/candidates.
 *
 * The ids are optional so callers can hold this hook while a node is still being chosen (or while the
 * blueprint resolves which node the parent even is); `enabled` keeps the query from running until all
 * of them are present, which is what the assertions inside rely on.
 */
export function useGraphRootNode(
  projectId: string | undefined,
  rootNodeId: string | undefined,
  proposalId: string | undefined,
  maps: TemplateMaps | undefined,
  templateNodeId: string | undefined
) {
  const { getDataNodeDetail } = useProjectNodesApi();

  // Memoized because React Query only reuses a previous `select` result while the function's identity
  // holds; an inline arrow re-runs it on *every* render of every consumer, and this one walks the whole
  // blueprint (`buildCandidateRelations`) and then deep-compares the result to restore identity. The
  // canvas re-renders on every React Flow node change, so that is per drag frame.
  const select = useCallback(
    (detail: DataNodeDetail): GraphNodeWithRelationsView => {
      const templateMaps = assertDefined(maps, 'template maps are required');
      const type = assertDefined(templateNodeId, 'templateNodeId is required');
      return {
        node: buildGraphNodeView(detail, type, templateMaps),
        candidates: buildCandidateRelations(type, templateMaps)
      };
    },
    [maps, templateNodeId]
  );

  return useQuery({
    queryKey: [...getGraphRootNodeQueryKeyPrefix(projectId, rootNodeId), proposalId, templateNodeId],
    queryFn: () =>
      resolve(
        getDataNodeDetail(
          assertDefined(projectId, 'projectId is required'),
          assertDefined(rootNodeId, 'rootNodeId is required'),
          assertDefined(templateNodeId, 'templateNodeId is required'),
          proposalId
        )
      ),
    enabled: Boolean(projectId) && Boolean(rootNodeId) && Boolean(templateNodeId) && Boolean(maps),
    select
  });
}
